import { gql, TypedDocumentNode } from '@apollo/client';

export interface AdminUnassignedPO {
  _id: string;
  sellerSiteName: string;
  buyerSiteName: string;
  acceptanceDate: string;
  poDueDate: string;
  poAssignedDate?: string;
  totalTransacted: number;
  availableCredit?: number;
  trucklaneCount: number;
  isVendorOfRecord: boolean;
  status: 'NOT SUBMITTED' | 'OVERDUE' | 'ASSIGNED';
}

type AdminUnassignedPOResult = {
  adminUnassignedPOQuery: AdminUnassignedPO[];
};

export const AdminUnassignedPOQuery: TypedDocumentNode<AdminUnassignedPOResult> = gql`
  query adminUnassignedPOQuery {
    adminUnassignedPOQuery {
      _id
      sellerSiteName
      buyerSiteName
      acceptanceDate
      poDueDate
      poAssignedDate
      totalTransacted
      availableCredit
      trucklaneCount
      isVendorOfRecord
      status
    }
  }
`;

interface Site {
  _id: string;
  siteName: string;
  siteType: string;
  physicalAddress: {
    streetAddress: string;
    city: string;
    stateCode: string;
    zipCode: string;
    countryCode: string;
  };
}

export interface POLineItem {
  _id: string;
  description: string;
  itemName: string;
  acceptanceDate: string;
  externalTransactionId: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  poNumber: string;
  trucklane: string;
}

export enum PO_STATUS {
  CREATED = 'CREATED',
  ASSIGNED = 'ASSIGNED',
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
}

export interface PurchaseOrder {
  _id: string;
  isVendorOfRecord: boolean;
  status: PO_STATUS;
  poDueDate: string;
  poAssignedDate: string;
  poNumber: string;
  sellerName: string;
  origin: PO_STATUS;
  buyerName: string;
  totalAmount: number;
}

export interface ExportGroupedOrderDetail {
  _id: string;
  purchaseOrders: PurchaseOrder[];
  lineItems: POLineItem[];
  sellerSites: Site[];
  buyerSites: Site[];
  creditLimit: number;
  availableCredit: number;
  awardSheets: {
    type: string;
    path: string;
  }[];
  packingListContacts: string[];
}

interface AdminOrderByExportRunResult {
  adminOrderByExportRunQuery: ExportGroupedOrderDetail;
}

export const AdminOrderByExportRunQuery: TypedDocumentNode<AdminOrderByExportRunResult, { exportRunId: string }> = gql`
  query adminOrderByExportRunQuery($exportRunId: String!) {
    adminOrderByExportRunQuery(exportRunId: $exportRunId) {
      _id
      purchaseOrders {
        _id
        isVendorOfRecord
        status
        poDueDate
        poNumber
      }
      lineItems {
        _id
        description
        itemName
        acceptanceDate
        externalTransactionId
        unitPrice
        quantity
        totalPrice
        poNumber
        trucklane
      }
      sellerSites {
        _id
        siteName
        siteType
        physicalAddress {
          streetAddress
          city
          stateCode
          zipCode
          countryCode
        }
      }
      buyerSites {
        _id
        siteName
        siteType
        physicalAddress {
          streetAddress
          city
          stateCode
          zipCode
          countryCode
        }
      }
      creditLimit
      availableCredit
      awardSheets {
        type
        path
      }
      packingListContacts
    }
  }
`;

interface AdminAssignedPOResult {
  adminAssignedPOQuery: PurchaseOrder[];
}

export const AdminAssignedPOQuery: TypedDocumentNode<AdminAssignedPOResult> = gql`
  query adminAssignedPOQuery {
    adminAssignedPOQuery {
      _id
      status
      poAssignedDate
      poNumber
      sellerName
      origin
      buyerName
      totalAmount
    }
  }
`;

export interface FullPurchaseOrder extends PurchaseOrder {
  buyerSiteId: string;
  sellerSiteId: string;
  externalOrderId?: string;
  logisticsTerm: string;
  lineItems: POLineItem[];
  fileAttachments: {
    type: string;
    path: string;
  }[];
  sellerLocation: Site;
  buyerLocation: Site;
  creditLimit: number;
  availableCredit: number;
  transactionServicesSettings: {
    packingListContacts: string[];
    takeRate: number;
    supplierPaymentDueDays: number;
    buyerPaymentDueDays: number;
  };
}

interface AdminPurchaseOrderByIdResult {
  adminPurchaseOrderByIdQuery: FullPurchaseOrder;
}

export const AdminPurchaseOrderByIdQuery: TypedDocumentNode<AdminPurchaseOrderByIdResult, { purchaseOrderId: string }> =
  gql`
  query adminPurchaseOrderByIdQuery($purchaseOrderId: String!) {
    adminPurchaseOrderByIdQuery(purchaseOrderId: $purchaseOrderId) {
      _id
      status
      poAssignedDate
      poNumber
      sellerName
      sellerSiteId
      buyerName
      buyerSiteId
      externalOrderId
      logisticsTerm
      creditLimit
      availableCredit
      lineItems {
        _id
        description
        itemName
        acceptanceDate
        externalTransactionId
        unitPrice
        quantity
        totalPrice
      }
      fileAttachments {
        type
        path
      }
      sellerLocation {
        _id
        siteName
        siteType
        physicalAddress {
          streetAddress
          city
          stateCode
          zipCode
          countryCode
        }
      }
      buyerLocation {
        _id
        siteName
        siteType
        physicalAddress {
          streetAddress
          city
          stateCode
          zipCode
          countryCode
        }
      }
      transactionServicesSettings {
        packingListContacts
        takeRate
        supplierPaymentDueDays
        buyerPaymentDueDays
      }
    }
  }
`;

export enum InvoiceStatus {
  OPEN = 'OPEN',
  VOIDED = 'VOIDED',
  PAID = 'PAID',
  PENDING = 'PENDING',
  DRAFT = 'DRAFT',
}

export enum POStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  READY_TO_SEND = 'READY_TO_SEND',
}

export interface AdminNumocoSingleOrderNakedRow {
  _id: string;
  status: string;
  name: string;
  created_at: string;
  buyer_company_name: string;
  buyer_site_id: string;
  supplier_company_name: string;
  seller_site_id: string;
  total_amount: number;
  order_number: number;
  delivery_address: string;
  pickup_address: string;
  purchase_orders: {
    id: string;
    status: POStatus;
    po_number: string;
    type: 'BUYER' | 'SUPPLIER' | 'NMC';
  }[];
  invoices: {
    id: string;
    status: InvoiceStatus;
    due_date: string;
    close_time: string;
    invoice_number: string;
    was_sent: boolean;
    total_amount: number;
  }[];
  documents?: {
    type: string;
    path: string;
    url: string;
  }[];
  packing_list_pdf_url: string;
}

export interface AdminNumocoSingleOrderNakedType {
  adminNumocoSingleOrderNaked: AdminNumocoSingleOrderNakedRow;
}

export const AdminNumocoSingleOrderNaked: TypedDocumentNode<AdminNumocoSingleOrderNakedType, { orderId: string }> = gql`
  query adminNumocoSingleOrderNaked($orderId: String!) {
    adminNumocoSingleOrderNaked(orderId: $orderId) {
      _id
      status
      name
      created_at
      buyer_company_name
      supplier_company_name
      total_amount
      order_number
      delivery_address
      pickup_address
      purchase_orders {
        id
        status
        po_number
        type
      }
      invoices {
        id
        status
        due_date
        close_time
        invoice_number
        was_sent
        total_amount
      }
      documents {
        type
        path
        url
      }
      packing_list_pdf_url
    }
  }
`;

export enum NUMOCO_SHIPMENT_INCOTERMS {
  EXW = 'EXW',
  FOB = 'FOB',
  FCA = 'FCA',
  CPT = 'CPT',
  CIP = 'CIP',
  DAP = 'DAP',
  DPU = 'DPU',
  DDP = 'DDP',
  FAS = 'FAS',
  CFR = 'CFR',
  CIF = 'CIF',
}

export enum NUMOCO_FREIGHT_PAYMENT_TERMS {
  PREPAYMENT = 'PREPAYMENT',
  FREIGHT_COLLECT = 'FREIGHT_COLLECT',
  MANAGED_BY_SUPPLIER = 'MANAGED_BY_SUPPLIER',
  MANAGED_BY_BUYER = 'MANAGED_BY_BUYER',
}

export enum NUMOCO_LOGISTICS_MODE {
  PLATFORM = 'PLATFORM',
  BUYER = 'BUYER',
  SUPPLIER = 'SUPPLIER',
}

export interface NUMOCOOrderTerms {
  is_prepaid: boolean;
  supplier_payment_due_days: number;
  buyer_payment_due_days: number;
  shipment_incoterms: NUMOCO_SHIPMENT_INCOTERMS;
  shipment_method: string;
  shipment_freight_payment_terms: NUMOCO_FREIGHT_PAYMENT_TERMS;
  logistics_mode: NUMOCO_LOGISTICS_MODE;
  take_rate_basis_points: number;
}

interface AdminNumocoPreviousOrder {
  _id: string;
  buyer_company_name: string;
  created_at: string;
  terms: NUMOCOOrderTerms;
}

export interface AdminNumocoPreviousOrderType {
  adminNumocoPreviousOrder: AdminNumocoPreviousOrder;
}

export const AdminNumocoPreviousOrder: TypedDocumentNode<AdminNumocoPreviousOrderType, { purchaseOrderId: string }> =
  gql`
  query adminNumocoPreviousOrder($purchaseOrderId: String!) {
    adminNumocoPreviousOrder(purchaseOrderId: $purchaseOrderId) {
      _id
      name
      created_at
      buyer_company_name
      terms {
        is_prepaid
        supplier_payment_due_days
        buyer_payment_due_days
        shipment_incoterms
        shipment_method
        shipment_freight_payment_terms
        logistics_mode
        take_rate_basis_points
      }
    }
  }
`;
